import {
    Box,
    Button as CButton,
    Input,
    Text as CText,
    Collapse,
    Flex,
    Grid,
    HStack,
    Icon,
    IconButton,
    Image,
    VStack,
} from '@chakra-ui/react';
import {
    LuArrowLeft as ArrowLeft,
    LuArrowRight as ArrowRight,
    LuChevronDown as ChevronDown,
    LuChevronRight as ChevronRight,
    LuEye as Eye,
    LuEyeOff as EyeOff,
    LuImage as ImageIcon,
    LuLock as Lock,
    LuMinus as Minus,
    LuPlus as Plus,
    LuRotateCw as RotateCw,
    LuSearch as Search,
    LuSquare as Square,
    LuX as X,
} from 'react-icons/lu';

import { Button, Card, Text } from '@/components/v4';
import DropDown from '@/components/forms/new/formikFormFields/DropDown';
import ResetButton from './ResetButton';
import { visitExpandBtnStyles } from 'pages/organisations/partners/edit/constants';

const BACKGROUND_TYPE_OPTIONS = [
    { id: 0, name: 'Default' },
    { id: 1, name: 'Solid Color' },
    { id: 2, name: 'Background Image' },
    { id: 3, name: 'Bing Image of the day' },
    { id: 4, name: 'NASA Image of the day' },
];

export default function LogosTab({
    darkMode,
    logoSet,
    defaultLogoSet,
    isLogosExpanded,
    setIsLogosExpanded,
    isBackgroundExpanded,
    setIsBackgroundExpanded,
    metaData,
    brandingSet,
    setBrandingSet,
    lightLogoInputRef,
    darkLogoInputRef,
    faviconInputRef,
    bgImageInputRef,
    colorPickerRef,
    handleLogoReset,
    previewTab,
    setPreviewTab,
    showPassword,
    setShowPassword,
    getPreviewBackground,
    isColorDark,
    styles,
}) {
    return (
        <VStack spacing={8} align="stretch" w="full">
            <Box>
                <CButton
                    variant="ghost"
                    color={darkMode ? 'white' : 'black'}
                    leftIcon={
                        <Icon
                            as={isLogosExpanded ? ChevronDown : ChevronRight}
                        />
                    }
                    onClick={() => setIsLogosExpanded(!isLogosExpanded)}
                    fontSize="lg"
                    fontWeight="semibold"
                    _hover={visitExpandBtnStyles}
                    _active={visitExpandBtnStyles}
                    mb={4}
                    px={0}>
                    Logos
                </CButton>
                <Collapse in={isLogosExpanded} animateOpacity>
                    <Card
                        borderRadius="8px"
                        boxShadow="0px 1px 3px rgba(0, 0, 0, 0.1)"
                        bg={darkMode ? 'dark.tcap' : null}
                        p={8}
                        shadow="sm">
                        <Grid
                            templateColumns={{
                                base: '1fr',
                                md: 'repeat(3, 1fr)',
                            }}
                            gap={8}>
                            {/* Light Logo */}
                            <VStack
                                bg={darkMode ? 'dark.bgDarkGray' : 'gray.50'}
                                borderRadius="lg"
                                p={5}
                                shadow="sm"
                                position="relative"
                                align="center"
                                spacing={4}>
                                <Text
                                    alignSelf="start"
                                    fontSize="xs"
                                    fontWeight="semibold"
                                    color="gray.500"
                                    marginBottom="16px">
                                    Light Logo (300 x 150 Max)
                                </Text>
                                {logoSet.light !== defaultLogoSet.light && (
                                    <ResetButton
                                        onReset={() => handleLogoReset('light')}
                                    />
                                )}
                                <Box
                                    w="full"
                                    maxW="300px"
                                    h="150px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    borderRadius="md"
                                    bg="white"
                                    p={4}
                                    shadow="inner"
                                    overflow="hidden">
                                    <Image
                                        src={logoSet.light}
                                        alt="Light Logo"
                                        maxW="full"
                                        maxH="full"
                                        objectFit="contain"
                                        fallbackSrc="https://via.placeholder.com/150?text=Logo"
                                    />
                                </Box>
                                <Text
                                    fontSize="10px"
                                    color="gray.400"
                                    fontWeight="bold"
                                    mt={4}
                                    letterSpacing="wider">
                                    {metaData.light.type} •{' '}
                                    {metaData.light.width} x{' '}
                                    {metaData.light.height} px
                                </Text>
                                <Button
                                    w="full"
                                    colorScheme="messenger"
                                    size="sm"
                                    mt={4}
                                    onClick={() =>
                                        lightLogoInputRef.current.click()
                                    }>
                                    Choose File
                                </Button>
                            </VStack>

                            {/* Dark Logo */}
                            <VStack
                                bg={darkMode ? 'dark.bgDarkGray' : 'gray.50'}
                                borderRadius="lg"
                                p={5}
                                shadow="sm"
                                position="relative"
                                align="center"
                                spacing={4}>
                                <Text
                                    alignSelf="start"
                                    fontSize="xs"
                                    fontWeight="semibold"
                                    color="gray.500"
                                    marginBottom="16px">
                                    Dark Logo (300 x 150 Max)
                                </Text>
                                {logoSet.dark !== defaultLogoSet.dark && (
                                    <ResetButton
                                        onReset={() => handleLogoReset('dark')}
                                    />
                                )}
                                <Box
                                    w="full"
                                    maxW="300px"
                                    h="150px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    borderRadius="md"
                                    bg="#262626"
                                    p={4}
                                    shadow="inner"
                                    overflow="hidden">
                                    <Image
                                        src={logoSet.dark}
                                        alt="Dark Logo"
                                        maxW="full"
                                        maxH="full"
                                        objectFit="contain"
                                        fallbackSrc="https://via.placeholder.com/150?text=Logo"
                                    />
                                </Box>
                                <Text
                                    fontSize="10px"
                                    color="gray.500"
                                    fontWeight="bold"
                                    mt={4}
                                    letterSpacing="wider">
                                    {metaData.dark.type} • {metaData.dark.width}{' '}
                                    x {metaData.dark.height} px
                                </Text>
                                <Button
                                    w="full"
                                    colorScheme="messenger"
                                    size="sm"
                                    mt={4}
                                    onClick={() =>
                                        darkLogoInputRef.current.click()
                                    }>
                                    Choose File
                                </Button>
                            </VStack>

                            {/* Favicon */}
                            <VStack
                                bg={darkMode ? 'dark.bgDarkGray' : 'gray.50'}
                                borderRadius="lg"
                                p={5}
                                shadow="sm"
                                position="relative"
                                align="center"
                                spacing={4}>
                                <Text
                                    alignSelf="start"
                                    fontSize="xs"
                                    fontWeight="semibold"
                                    color="gray.500"
                                    marginBottom="16px">
                                    Favicon (192x192 Max)
                                </Text>
                                {logoSet.favicon !== defaultLogoSet.favicon && (
                                    <ResetButton
                                        onReset={() =>
                                            handleLogoReset('favicon')
                                        }
                                    />
                                )}
                                <Box
                                    w="150px"
                                    h="150px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    borderRadius="md"
                                    bg="white"
                                    p={4}
                                    shadow="inner"
                                    overflow="hidden">
                                    <Image
                                        src={logoSet.favicon}
                                        alt="Favicon"
                                        boxSize="full"
                                        objectFit="contain"
                                        fallbackSrc="https://via.placeholder.com/150?text=Fav"
                                    />
                                </Box>
                                <Text
                                    fontSize="10px"
                                    color="gray.400"
                                    fontWeight="bold"
                                    mt={4}
                                    letterSpacing="wider">
                                    {metaData.favicon.type} •{' '}
                                    {metaData.favicon.width} x{' '}
                                    {metaData.favicon.height} px
                                </Text>
                                <Button
                                    w="full"
                                    mt={4}
                                    colorScheme="messenger"
                                    size="sm"
                                    onClick={() =>
                                        faviconInputRef.current.click()
                                    }>
                                    Choose File
                                </Button>
                            </VStack>
                        </Grid>
                    </Card>
                </Collapse>
            </Box>

            <Box>
                <CButton
                    variant="ghost"
                    color={darkMode ? 'white' : 'black'}
                    leftIcon={
                        <Icon
                            as={
                                isBackgroundExpanded
                                    ? ChevronDown
                                    : ChevronRight
                            }
                        />
                    }
                    onClick={() =>
                        setIsBackgroundExpanded(!isBackgroundExpanded)
                    }
                    fontSize="lg"
                    fontWeight="semibold"
                    _hover={visitExpandBtnStyles}
                    _active={visitExpandBtnStyles}
                    mb={4}
                    px={0}>
                    Login Background
                </CButton>
                <Collapse in={isBackgroundExpanded} animateOpacity>
                    <VStack
                        bg={darkMode ? 'dark.tcap' : 'white'}
                        borderRadius="8px"
                        p={8}
                        shadow="sm"
                        spacing={6}
                        align="stretch">
                        <Flex align="center">
                            <Text
                                w="48"
                                fontSize="sm"
                                fontWeight="semibold"
                                color="gray.700">
                                Background Type
                            </Text>
                            <Box flex={1} position="relative">
                                <DropDown
                                    data={BACKGROUND_TYPE_OPTIONS}
                                    shownValue="name"
                                    currentId={brandingSet.backgroundType}
                                    alphabetical={false}
                                    noPadding
                                    darkMode={darkMode}
                                    handleSelectChange={(item) =>
                                        setBrandingSet((p) => ({
                                            ...p,
                                            backgroundType: Number(item?.id),
                                        }))
                                    }
                                />
                            </Box>
                        </Flex>

                        {brandingSet.backgroundType === 1 && (
                            <Flex align="center">
                                <Text
                                    w="48"
                                    fontSize="sm"
                                    fontWeight="semibold"
                                    color="gray.700">
                                    Background Colour
                                </Text>
                                <Box flex={1} position="relative">
                                    <Input
                                        value={brandingSet.backgroundColor}
                                        onClick={() =>
                                            colorPickerRef.current.click()
                                        }
                                        readOnly
                                        backgroundColor={`${brandingSet.backgroundColor} !important`}
                                        color={
                                            isColorDark(
                                                brandingSet.backgroundColor,
                                            )
                                                ? 'white'
                                                : 'black'
                                        }
                                        fontSize="sm"
                                        fontWeight="bold"
                                        textTransform="uppercase"
                                        letterSpacing="wider"
                                        shadow="sm"
                                        borderColor="transparent"
                                        _hover={{
                                            borderColor: 'transparent',
                                        }}
                                    />
                                    <input
                                        type="color"
                                        ref={colorPickerRef}
                                        value={brandingSet.backgroundColor}
                                        onChange={(e) =>
                                            setBrandingSet((p) => ({
                                                ...p,
                                                backgroundColor: e.target.value,
                                            }))
                                        }
                                        style={{
                                            position: 'absolute',
                                            inset: 0,
                                            opacity: 0,
                                            cursor: 'pointer',
                                            width: '100%',
                                            height: '100%',
                                        }}
                                    />
                                </Box>
                            </Flex>
                        )}

                        {brandingSet.backgroundType === 2 && (
                            <Flex align="flex-start">
                                <Text
                                    w="48"
                                    fontSize="sm"
                                    fontWeight="semibold"
                                    color="gray.700"
                                    pt={2}>
                                    Background Image
                                </Text>
                                <HStack flex={1} spacing={6} align="center">
                                    <Box
                                        w="48"
                                        h="32"
                                        bg="gray.50"
                                        border="1px dashed"
                                        borderColor="gray.300"
                                        borderRadius="md"
                                        overflow="hidden"
                                        flexShrink={0}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center">
                                        {brandingSet.backgroundImage ? (
                                            <Image
                                                src={
                                                    brandingSet.backgroundImage
                                                }
                                                boxSize="full"
                                                objectFit="cover"
                                            />
                                        ) : (
                                            <Icon
                                                as={ImageIcon}
                                                boxSize={6}
                                                color="gray.300"
                                            />
                                        )}
                                    </Box>
                                    <Button
                                        colorScheme="messenger"
                                        size="sm"
                                        px={6}
                                        flexShrink={0}
                                        onClick={() =>
                                            bgImageInputRef.current.click()
                                        }>
                                        Choose PNG
                                    </Button>
                                    <Box minW={0}>
                                        {metaData.bg && (
                                            <Text
                                                fontSize="10px"
                                                color="gray.400"
                                                fontWeight="bold"
                                                letterSpacing="wider">
                                                {metaData.bg}
                                            </Text>
                                        )}
                                    </Box>
                                </HStack>
                            </Flex>
                        )}
                    </VStack>
                </Collapse>
            </Box>

            <Box>
                <Text
                    as="h3"
                    fontSize="18px"
                    color={darkMode ? 'white' : 'black'}
                    mb={6}
                    fontWeight="semibold">
                    Login Screen Preview
                </Text>
                {/* Browser Frame */}
                <Card
                    bg="gray.50"
                    borderRadius="md"
                    overflow="hidden"
                    shadow="2xl">
                    {/* Tab Bar */}
                    <Box bg="#DEE1E6">
                        <Flex h="10" align="flex-end">
                            <Flex
                                align="center"
                                bg="white"
                                borderTopRadius="lg"
                                ml={2}
                                px={3}
                                h="9"
                                border="1px solid"
                                borderColor="gray.200"
                                borderBottom="none"
                                minW="180px"
                                gap={2}
                                shadow="sm">
                                <Image
                                    src={logoSet.favicon}
                                    boxSize="4"
                                    objectFit="contain"
                                    fallbackSrc="https://via.placeholder.com/150"
                                />
                                <CText
                                    fontSize="10px"
                                    fontWeight="medium"
                                    color="gray.600"
                                    isTruncated
                                    flex={1}>
                                    TCAP - Preview
                                </CText>
                                <Icon
                                    as={X}
                                    boxSize="3.5"
                                    color="gray.400"
                                    p={0.5}
                                    cursor="pointer"
                                    _hover={{
                                        bg: 'gray.100',
                                        color: 'gray.600',
                                        borderRadius: 'full',
                                    }}
                                />
                            </Flex>
                            <Box
                                p={1.5}
                                ml={1}
                                color="gray.500"
                                cursor="pointer"
                                _hover={{
                                    bg: 'gray.300',
                                    borderRadius: 'full',
                                }}>
                                <Plus size={14} />
                            </Box>
                            <Box flex={1} aria-label="Spacer Box" />
                            <HStack h="full" spacing={0}>
                                <Flex
                                    w="12"
                                    h="10"
                                    align="center"
                                    justify="center"
                                    _hover={{ bg: 'gray.200' }}>
                                    <Minus size={16} />
                                </Flex>
                                <Flex
                                    w="12"
                                    h="10"
                                    align="center"
                                    justify="center"
                                    _hover={{ bg: 'gray.200' }}>
                                    <Square size={12} />
                                </Flex>
                                <Flex
                                    w="12"
                                    h="10"
                                    align="center"
                                    justify="center"
                                    _hover={{
                                        bg: 'red.500',
                                        color: 'white',
                                    }}>
                                    <X size={16} />
                                </Flex>
                            </HStack>
                        </Flex>
                        {/* URL Bar */}
                        <Flex
                            align="center"
                            gap={3}
                            px={3}
                            h="40px"
                            bg="white"
                            borderBottom="1px solid"
                            borderColor="gray.50">
                            <HStack spacing={1.5} color="gray.500">
                                <Icon as={ArrowLeft} opacity={0.4} />
                                <Icon as={ArrowRight} opacity={0.4} />
                                <Icon
                                    as={RotateCw}
                                    boxSize={3.5}
                                    cursor="pointer"
                                    _hover={{
                                        bg: 'gray.100',
                                        borderRadius: 'full',
                                    }}
                                />
                            </HStack>
                            <Flex
                                flex={1}
                                align="center"
                                bg="#F1F3F4"
                                borderRadius="full"
                                h="8"
                                px={4}
                                gap={3}
                                overflow="hidden">
                                <Icon as={Lock} boxSize={3} color="gray.500" />
                                <CText
                                    fontSize="11px"
                                    color="gray.900"
                                    isTruncated>
                                    {`${window.location.origin}/ui/`}
                                </CText>
                            </Flex>
                            <HStack spacing={2} color="gray.500">
                                <Icon
                                    as={Search}
                                    boxSize={4}
                                    cursor="pointer"
                                    _hover={{
                                        bg: 'gray.100',
                                        borderRadius: 'full',
                                    }}
                                />
                                <Box
                                    boxSize="7"
                                    borderRadius="full"
                                    overflow="hidden"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    cursor="pointer">
                                    <Image src={logoSet.favicon} />
                                </Box>
                            </HStack>
                        </Flex>
                    </Box>
                    {/* Preview Content */}
                    <Flex overflow="hidden">
                        <Box
                            flex={1}
                            transition="all 1s"
                            {...getPreviewBackground()}
                        />
                        <Box
                            w="500px"
                            bg="white"
                            borderLeft="1px solid"
                            borderColor="gray.200"
                            display="flex"
                            flexDirection="column"
                            p="7"
                            px="14">
                            <Box mb={7} display="flex" justifyContent="center">
                                <Image
                                    src={logoSet.light}
                                    maxW="300px"
                                    maxH="150px"
                                    objectFit="contain"
                                    fallbackSrc="https://via.placeholder.com/150"
                                />
                            </Box>
                            <Flex
                                borderBottom="1px solid"
                                borderColor="gray.200"
                                mb={10}>
                                <Button
                                    flex={1}
                                    variant="unstyled"
                                    h="14"
                                    fontSize="sm"
                                    fontWeight="bold"
                                    borderBottom="2px solid"
                                    borderRadius="none"
                                    borderColor={
                                        previewTab === 'Login'
                                            ? 'messenger.500'
                                            : 'transparent'
                                    }
                                    color={
                                        previewTab === 'Login'
                                            ? 'messenger.500'
                                            : 'gray.400'
                                    }
                                    onClick={() => setPreviewTab('Login')}>
                                    Login
                                </Button>
                                <Button
                                    flex={1}
                                    isDisabled
                                    variant="unstyled"
                                    h="14"
                                    fontSize="sm"
                                    fontWeight="bold"
                                    borderBottom="2px solid"
                                    borderRadius="none"
                                    borderColor={
                                        previewTab === 'Register'
                                            ? 'messenger.500'
                                            : 'transparent'
                                    }
                                    color={
                                        previewTab === 'Register'
                                            ? 'messenger.500'
                                            : 'gray.400'
                                    }
                                    onClick={() => setPreviewTab('Register')}>
                                    Register
                                </Button>
                            </Flex>
                            <VStack spacing={6} align="stretch">
                                <Box>
                                    <Text
                                        fontSize="xs"
                                        fontWeight="bold"
                                        color="gray.600"
                                        mb={2}
                                        letterSpacing="wide">
                                        Email{' '}
                                        <Text as="span" color="red.500">
                                            *
                                        </Text>
                                    </Text>
                                    <Input
                                        placeholder="Enter your email"
                                        name="email"
                                        readOnly
                                        _css={styles.field}
                                    />
                                </Box>
                                <Box position="relative">
                                    <Text
                                        fontSize="xs"
                                        fontWeight="bold"
                                        color="gray.600"
                                        mb={2}
                                        letterSpacing="wide">
                                        Password{' '}
                                        <Text as="span" color="red.500">
                                            *
                                        </Text>
                                    </Text>
                                    <Input
                                        type={
                                            showPassword ? 'text' : 'password'
                                        }
                                        name="password"
                                        placeholder="Enter your password"
                                        readOnly
                                        _css={styles.field}
                                        pr="12"
                                    />
                                    <IconButton
                                        position="absolute"
                                        right="2"
                                        variant="ghost"
                                        icon={
                                            <Icon
                                                as={showPassword ? EyeOff : Eye}
                                            />
                                        }
                                        onClick={() =>
                                            setShowPassword(!showPassword)
                                        }
                                        color="gray.400"
                                        _hover={{ color: 'gray.600' }}
                                    />
                                </Box>
                            </VStack>
                        </Box>
                    </Flex>
                </Card>
            </Box>
        </VStack>
    );
}
